/**
 * DashboardLinkWidget - Creates a link to another user's dashboard
 * Useful for executives to link to team member dashboards
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Skeleton } from '../../ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { cn } from '../../../lib/utils';
import {
  LayoutDashboard,
  ExternalLink,
  User,
  Loader2,
  Link2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configuration Dialog Component
export const DashboardLinkConfigDialog = ({ open, onOpenChange, config, onSave }) => {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDashboard, setSelectedDashboard] = useState(config?.dashboardId || '');
  const [customLabel, setCustomLabel] = useState(config?.customLabel || '');
  const [customDescription, setCustomDescription] = useState(config?.customDescription || '');

  // Fetch available dashboards
  useEffect(() => {
    const fetchDashboards = async () => {
      setLoading(true);
      try {
        // Fetch all dashboards (admin can see all)
        const response = await fetch(`${API_URL}/api/dashboards/all`);
        if (response.ok) {
          const data = await response.json();
          setDashboards(data.dashboards || []);
        }
      } catch (err) {
        console.error('Failed to fetch dashboards:', err);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchDashboards();
      setSelectedDashboard(config?.dashboardId || '');
      setCustomLabel(config?.customLabel || '');
      setCustomDescription(config?.customDescription || '');
    }
  }, [open, config]);

  const handleSave = () => {
    const selectedDash = dashboards.find(d => d.id === selectedDashboard);
    onSave({
      dashboardId: selectedDashboard,
      dashboardTitle: selectedDash?.title || 'Dashboard',
      ownerEmail: selectedDash?.owner_email || '',
      customLabel: customLabel.trim(),
      customDescription: customDescription.trim(),
    });
    onOpenChange(false);
  };

  const selectedDash = dashboards.find(d => d.id === selectedDashboard);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Configure Dashboard Link</DialogTitle>
          <DialogDescription>
            Select a dashboard to link to
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Dashboard</Label>
                <Select value={selectedDashboard} onValueChange={setSelectedDashboard}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a dashboard..." />
                  </SelectTrigger>
                  <SelectContent>
                    {dashboards.map((dash) => (
                      <SelectItem key={dash.id} value={dash.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span>{dash.title}</span>
                          <span className="text-xs text-muted-foreground">
                            ({dash.owner_email})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Custom Label (optional)</Label>
                <Input
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  placeholder={selectedDash?.title || "e.g., IT Executive Dashboard"}
                />
                <p className="text-xs text-muted-foreground">
                  Override the dashboard title shown on the widget
                </p>
              </div>

              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  placeholder="e.g., View the main IT metrics dashboard"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!selectedDashboard || loading}>
            Save Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Widget Component
export const DashboardLinkWidget = ({ config, onConfigure }) => {
  const [loading, setLoading] = useState(false);

  const dashboardId = config?.dashboardId;
  const label = config?.customLabel || config?.dashboardTitle || 'Dashboard';
  const description = config?.customDescription || `View ${config?.ownerEmail || 'user'}'s dashboard`;

  const handleClick = () => {
    if (!dashboardId) {
      if (onConfigure) onConfigure();
      return;
    }

    // Store the target dashboard ID and trigger navigation
    sessionStorage.setItem('viewDashboardId', dashboardId);
    sessionStorage.setItem('viewDashboardLabel', label);
    
    // Trigger a page reload to the view mode
    // The App.js will check for this and show the dashboard
    window.location.href = `/?view=${dashboardId}`;
  };

  if (!dashboardId) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-4">
        <Link2 className="w-10 h-10 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground mb-3">No dashboard selected</p>
        <Button variant="outline" size="sm" onClick={() => onConfigure?.()}>
          Configure Link
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div 
        className="flex-1 flex flex-col items-center justify-center p-4 cursor-pointer hover:bg-accent/50 rounded-lg transition-colors"
        onClick={handleClick}
      >
        <div className="p-4 rounded-full bg-primary/10 mb-4">
          <LayoutDashboard className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-center mb-1">{label}</h3>
        <p className="text-sm text-muted-foreground text-center mb-4">{description}</p>
        <Button variant="default" className="gap-2">
          <ExternalLink className="w-4 h-4" />
          View Dashboard
        </Button>
      </div>
    </div>
  );
};

export default DashboardLinkWidget;
